/**
 * HPBar — the iconic Pokémon HP gauge: a recessed dark track holding a fill that
 * shifts green → yellow → red as HP drops (color logic lives in theme.hpColor, so it
 * is unit-tested). Clean, slightly rounded, with a small "HP" tag and an optional
 * numeric label — a tasteful GB/GBC nod, not a crunchy pixel restyle.
 *
 * Public API is intentionally tiny so scenes can adopt it without ceremony:
 *   const bar = new HPBar(scene, x, y, { width });
 *   bar.setRatio(cur / max);            // instant color + width
 *   bar.setHp(cur, max);                // also updates the "12/35" label
 *   bar.tweenTo(ratio, ms, reduced);    // smooth drain (BattleScene drama)
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, hpColor } from './theme.js';

export interface HPBarOptions {
  /** Total width of the gauge (the track). */
  readonly width: number;
  /** Track/fill thickness. Defaults to a clean 10px. */
  readonly height?: number;
  /** Show the leading "HP" tag to the left of the gauge (default true). */
  readonly showLabel?: boolean;
  /** Show a "cur/max" numeric readout under the gauge (default false). */
  readonly showNumbers?: boolean;
  /** Font size for the HP tag + numbers. */
  readonly fontSize?: number;
}

export class HPBar extends Phaser.GameObjects.Container {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly numbers?: Phaser.GameObjects.Text;
  private readonly barWidth: number;
  private readonly barHeight: number;
  private readonly barX: number;
  private ratio = 1;

  constructor(scene: Phaser.Scene, x: number, y: number, options: HPBarOptions) {
    super(scene, x, y);
    const showLabel = options.showLabel ?? true;
    const fontSize = options.fontSize ?? FONT.size.xs;
    this.barHeight = options.height ?? 10;

    let leftPad = 0;
    if (showLabel) {
      const tag = scene.add
        .text(0, 0, 'HP', {
          fontFamily: FONT.family,
          fontSize: `${fontSize}px`,
          color: CSS.boxTextMuted,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5);
      this.add(tag);
      leftPad = tag.width + 6;
    }
    this.barX = leftPad;
    this.barWidth = Math.max(8, options.width - leftPad);

    this.gfx = scene.add.graphics();
    this.add(this.gfx);

    if (options.showNumbers) {
      this.numbers = scene.add
        .text(this.barX + this.barWidth, this.barHeight + 3, '', {
          fontFamily: FONT.mono,
          fontSize: `${fontSize}px`,
          color: CSS.boxTextMuted,
        })
        .setOrigin(1, 0);
      this.add(this.numbers);
    }

    this.redraw();
    scene.add.existing(this);
  }

  /** Set the HP ratio (0..1) instantly — updates fill width + color band. */
  setRatio(ratio: number): this {
    this.ratio = Math.max(0, Math.min(1, ratio));
    this.redraw();
    return this;
  }

  /** Set current/max HP — updates the gauge and, if shown, the numeric readout. */
  setHp(current: number, max: number): this {
    const safeMax = Math.max(1, max);
    this.numbers?.setText(`${Math.max(0, Math.round(current))}/${max}`);
    return this.setRatio(current / safeMax);
  }

  /** Smoothly drain/fill to `ratio` over `durationMs` (instant under reducedMotion). */
  tweenTo(ratio: number, durationMs: number, reducedMotion = false): this {
    const target = Math.max(0, Math.min(1, ratio));
    if (reducedMotion || durationMs <= 0) return this.setRatio(target);
    const proxy = { v: this.ratio };
    this.scene.tweens.add({
      targets: proxy,
      v: target,
      duration: durationMs,
      ease: 'Sine.easeOut',
      onUpdate: () => this.setRatio(proxy.v),
      onComplete: () => this.setRatio(target),
    });
    this.ratio = target;
    return this;
  }

  /** The current ratio (0..1). */
  get hpRatio(): number {
    return this.ratio;
  }

  private redraw(): void {
    const g = this.gfx;
    const w = this.barWidth;
    const h = this.barHeight;
    const r = Math.min(h / 2, 4);
    g.clear();
    // Recessed dark track.
    g.fillStyle(COLORS.hpTrack, 1);
    g.fillRoundedRect(this.barX, 0, w, h, r);
    // The green→yellow→red fill.
    const fillW = w * this.ratio;
    if (fillW > 1) {
      g.fillStyle(hpColor(this.ratio), 1);
      g.fillRoundedRect(this.barX, 0, fillW, h, r);
    }
    // A crisp thin frame (the classic boxed look).
    g.lineStyle(1.5, COLORS.hpFrame, 1);
    g.strokeRoundedRect(this.barX, 0, w, h, r);
  }
}
